// src/redux/slices/lotAndContainerDetailsSlice.ts
import { createDrawerSlice, IBaseDrawerState } from "../createDrawerSlice";

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { csl } from "lobbie";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants";
import { createDrawerActionMeta, createDrawerThunkMeta } from "../stackableDrawerConstant";

export interface ILotAndContainerDetailsState extends IBaseDrawerState {
    lotId?: number;
    containerId?: number;
    lotDetails: csl.ILotFullDetails | null;
    containerDetails: csl.IContainerFullDetails | null;
    isLoading: boolean;
    error: string | null;
    lotTableRefetchTrigger: number;
    containerLogsRefetchTrigger: number;
}

export const initialStateLotAndContainerDetails: ILotAndContainerDetailsState = {
    drawerHistoryInstanceId: "",
    show: false,
    lotId: undefined,
    containerId: undefined,
    lotDetails: null,
    containerDetails: null,
    isLoading: false,
    error: null,
    lotTableRefetchTrigger: 0,
    containerLogsRefetchTrigger: 0,
};

export const fetchLotDetails = createAsyncThunk(
    "lotAndContainerDetails/fetchLotDetails",
    async (lotId: number, { rejectWithValue }) => {
        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/lot/${lotId}`;
            const { data } = await axios.get(route, { withCredentials: true });
            return data as csl.ILotFullDetails;
        } catch (error) {
            return rejectWithValue("Failed to fetch lot details");
        }
    },
);

export const fetchContainerDetails = createAsyncThunk(
    "lotAndContainerDetails/fetchContainerDetails",
    async (containerId: number, { rejectWithValue }) => {
        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/${containerId}`;
            const { data } = await axios.get(route, { withCredentials: true });
            return data as csl.IContainerFullDetails;
        } catch (error) {
            return rejectWithValue("Failed to fetch container details");
        }
    },
);

export const showLotAndContainerDetails = createAsyncThunk(
    "lotAndContainerDetails/showLotandContainerDetails",
    async (payload: { lotId?: number; containerId?: number }, { dispatch }) => {
        // Conditionally fetch the appropriate details
        if (payload.containerId) {
            await dispatch(fetchContainerDetails(payload.containerId));
        } else if (payload.lotId) {
            await dispatch(fetchLotDetails(payload.lotId));
        }
        return {
            ...payload,
        };
    },
    createDrawerThunkMeta("show"),
);

const lotAndContainerDetailsSlice = createDrawerSlice({
    name: "lotAndContainerDetails",
    initialState: initialStateLotAndContainerDetails,
    reducers: {
        hideLotAndContainerDetails: {
            prepare: () => ({
                payload: undefined,
                meta: createDrawerActionMeta("hide"),
            }),
            reducer: (state) => {
                state.show = false;
            },
        },
        resetData: (state) => {
            state.lotDetails = null;
            state.containerDetails = null;
            state.error = null;
        },
        refreshLotContainersTable: (state) => {
            state.lotTableRefetchTrigger += 1;
        },
        refreshContainerLogsTable: (state) => {
            state.containerLogsRefetchTrigger += 1;
        },
    },
    extraReducers: (builder) => {
        // Handle showing the drawer
        builder
            .addCase(showLotAndContainerDetails.pending, (state, action) => {
                let drawerHistoryInstanceId = "lotAndContainerDetails";
                if (action.meta.arg.lotId)
                    drawerHistoryInstanceId += `-lot${action.meta.arg.lotId}`;
                if (action.meta.arg.containerId)
                    drawerHistoryInstanceId += `-container${action.meta.arg.containerId}`;
                return {
                    ...initialStateLotAndContainerDetails,
                    show: true, // so the drawer doesn't temporarily close if you're opening a container details after a lot details drawer
                    isLoading: true,
                    error: null,
                    drawerHistoryInstanceId,
                };
            })
            .addCase(showLotAndContainerDetails.fulfilled, (state, action) => {
                // Set drawer state
                state.lotId = action.payload.lotId;
                state.containerId = action.payload.containerId;
                state.show = true;
                if (action.payload.containerId) {
                    state.lotDetails = null;
                    state.containerLogsRefetchTrigger += 1;
                } else {
                    state.containerDetails = null;
                    state.lotTableRefetchTrigger += 1;
                }
                state.isLoading = false;
            })
            .addCase(showLotAndContainerDetails.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            // Handle lot details loading states
            .addCase(fetchLotDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchLotDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.lotDetails = action.payload;
            })
            .addCase(fetchLotDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Handle container details loading states
            .addCase(fetchContainerDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchContainerDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.containerDetails = action.payload;
            })
            .addCase(fetchContainerDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    hideLotAndContainerDetails,
    resetData,
    refreshLotContainersTable,
    refreshContainerLogsTable,
} = lotAndContainerDetailsSlice.actions;

export default lotAndContainerDetailsSlice.reducer;
